import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, X } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

interface HistoryDrinkModalProps {
  open: boolean;
  onClose: () => void;
  onSubmitted: () => void;
}

export function HistoryDrinkModal({ open, onClose, onSubmitted }: HistoryDrinkModalProps) {
  const { user, group, membership } = useAuth();
  const { t } = useLanguage();
  const queryClient = useQueryClient();

  const [beerCount, setBeerCount] = useState(0);
  const [wineCount, setWineCount] = useState(0);
  const [shotCount, setShotCount] = useState(0);
  const [lastDrinkDate, setLastDrinkDate] = useState<Date | undefined>();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open || !user || !group || !membership) return null;

  const challengeStart = new Date(group.challenge_start);
  challengeStart.setHours(0, 0, 0, 0);

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  yesterday.setHours(0, 0, 0, 0);

  const totalDrinks = beerCount + wineCount + shotCount;

  const handleSubmit = async () => {
    setError(null);

    if (totalDrinks === 0 && !lastDrinkDate) {
      setError(t('historyErrorEmpty'));
      return;
    }

    if (totalDrinks > 0 && !lastDrinkDate) {
      setError(t('historyErrorDateRequired'));
      return;
    }

    setSubmitting(true);

    try {
      // Distribute drinks backwards ending at selected date
      if (totalDrinks > 0 && lastDrinkDate) {
        const drinkInserts: Array<{
          user_id: string;
          group_id: string;
          drink_type: 'beer' | 'wine' | 'shot';
          logged_at: string;
        }> = [];

        // Build array of all drinks to insert
        const allDrinks: Array<'beer' | 'wine' | 'shot'> = [];
        for (let i = 0; i < beerCount; i++) allDrinks.push('beer');
        for (let i = 0; i < wineCount; i++) allDrinks.push('wine');
        for (let i = 0; i < shotCount; i++) allDrinks.push('shot');

        // Calculate available days from challenge_start to lastDrinkDate
        const startDate = new Date(challengeStart);
        const endDate = new Date(lastDrinkDate);
        const availableDays = Math.max(1, Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1);

        // Distribute drinks evenly across available days, ending at lastDrinkDate
        for (let i = 0; i < allDrinks.length; i++) {
          // Spread drinks across the date range
          const dayOffset = Math.floor((i / allDrinks.length) * availableDays);
          const drinkDate = new Date(startDate);
          drinkDate.setDate(drinkDate.getDate() + dayOffset);
          drinkDate.setHours(12, 0, 0, 0); // noon to avoid timezone issues

          drinkInserts.push({
            user_id: user.id,
            group_id: group.id,
            drink_type: allDrinks[i],
            logged_at: drinkDate.toISOString(),
          });
        }

        const { error: insertError } = await supabase
          .from('drink_logs')
          .insert(drinkInserts);

        if (insertError) throw insertError;
      }

      // Update membership to mark history as imported
      const { error: updateError } = await supabase
        .from('memberships')
        .update({
          history_imported: true,
          history_imported_at: new Date().toISOString(),
          history_last_drink_date: lastDrinkDate ? format(lastDrinkDate, 'yyyy-MM-dd') : null,
        })
        .eq('id', membership.id);

      if (updateError) throw updateError;

      // Invalidate all relevant queries
      queryClient.invalidateQueries({ queryKey: ['drink-logs'] });
      queryClient.invalidateQueries({ queryKey: ['leaderboard'] });
      queryClient.invalidateQueries({ queryKey: ['challenge-drink-logs'] });
      queryClient.invalidateQueries({ queryKey: ['all-drink-logs'] });
      queryClient.invalidateQueries({ queryKey: ['user-streak'] });
      queryClient.invalidateQueries({ queryKey: ['personal-streak'] });
      queryClient.invalidateQueries({ queryKey: ['user-drink-count'] });

      onSubmitted();
    } catch (err) {
      console.error('History import failed:', err);
      setError(t('historyErrorFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const clamp = (val: string, max: number) => {
    const n = parseInt(val, 10);
    if (isNaN(n) || n < 0) return 0;
    return Math.min(n, max);
  };

  return (
    <div className="fixed inset-0 z-50 bg-background flex flex-col">
      {/* Header with close */}
      <div className="flex justify-end p-4">
        <button onClick={onClose} className="p-2 rounded-sm opacity-70 hover:opacity-100 transition-opacity">
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6 pb-6 max-w-lg mx-auto w-full">
        <h2 className="text-xl font-serif font-bold text-center mb-6">
          {t('historyTitle')}
        </h2>

        {/* Section A: Drink counts */}
        <div className="space-y-4 mb-8">
          <div className="flex items-center gap-4">
            <span className="text-3xl">🍺</span>
            <Input
              type="number"
              min={0}
              max={365}
              value={beerCount || ''}
              onChange={(e) => setBeerCount(clamp(e.target.value, 365))}
              placeholder="0"
              className="w-24 text-center text-lg"
            />
          </div>
          <div className="flex items-center gap-4">
            <span className="text-3xl">🍷</span>
            <Input
              type="number"
              min={0}
              max={365}
              value={wineCount || ''}
              onChange={(e) => setWineCount(clamp(e.target.value, 365))}
              placeholder="0"
              className="w-24 text-center text-lg"
            />
          </div>
          <div className="flex items-center gap-4">
            <span className="text-3xl">🥃</span>
            <Input
              type="number"
              min={0}
              max={365}
              value={shotCount || ''}
              onChange={(e) => setShotCount(clamp(e.target.value, 365))}
              placeholder="0"
              className="w-24 text-center text-lg"
            />
          </div>
        </div>

        {/* Section B: Last drink date */}
        <div className="mb-8">
          <label className="text-sm font-medium mb-2 block">
            {t('historyDateLabel')}
          </label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !lastDrinkDate && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {lastDrinkDate ? format(lastDrinkDate, 'dd.MM.yyyy') : t('historyDatePlaceholder')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={lastDrinkDate}
                onSelect={setLastDrinkDate}
                disabled={(date) => {
                  const d = new Date(date);
                  d.setHours(0, 0, 0, 0);
                  return d > yesterday || d < challengeStart;
                }}
                initialFocus
                className={cn("p-3 pointer-events-auto")}
              />
            </PopoverContent>
          </Popover>
        </div>

        {error && (
          <p className="text-sm text-destructive mb-4 text-center">{error}</p>
        )}
      </div>

      {/* Footer buttons */}
      <div className="p-4 border-t border-border max-w-lg mx-auto w-full space-y-2">
        <Button
          onClick={handleSubmit}
          disabled={submitting}
          className="w-full"
        >
          {submitting ? t('historySubmitting') : t('historySubmit')}
        </Button>
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={submitting}
          className="w-full"
        >
          {t('historyClose')}
        </Button>
      </div>
    </div>
  );
}
